import React, { useRef, useEffect, useCallback } from 'react';

interface PendingRefinement {
  id: string;
  text: string;
}

export interface SelectionRange {
  start: number;
  end: number;
}

interface EditableTranscriptionProps {
  value: string;
  onChange: (value: string) => void;
  provisionalText: string;
  isRecording: boolean;
  onCursorPositionChange?: (position: number) => void;
  onSelectionChange?: (range: SelectionRange) => void;
  targetCursorPosition?: number | null; // When set, moves cursor to this position after value update
  className?: string;
  pendingRefinements?: PendingRefinement[];
  editorRef?: React.RefObject<HTMLDivElement | null>; // External ref for accessing editor DOM
}

/**
 * Contenteditable transcription editor with cursor tracking.
 * - Allows user to edit transcribed text
 * - Tracks cursor position for inserting new transcription
 * - Shows provisional text at cursor position
 */
export function EditableTranscription({
  value,
  onChange,
  provisionalText,
  isRecording,
  onCursorPositionChange,
  onSelectionChange,
  targetCursorPosition = null,
  className = '',
  pendingRefinements = [],
  editorRef: externalEditorRef,
}: EditableTranscriptionProps) {
  const internalEditorRef = useRef<HTMLDivElement>(null);
  // Use external ref if provided, otherwise use internal
  const editorRef = externalEditorRef || internalEditorRef;
  const containerRef = useRef<HTMLDivElement>(null);
  const cursorOverlayRef = useRef<HTMLDivElement>(null);
  const cursorPositionRef = useRef<number>(value.length);
  const lastValueRef = useRef<string>(value);
  const isRecordingRef = useRef(isRecording);
  isRecordingRef.current = isRecording;

  // Get current selection range (start and end positions) in the contenteditable
  // biome-ignore lint/correctness/useExhaustiveDependencies: value.length is intentionally used as fallback
  const getSelectionRange = useCallback((): SelectionRange => {
    const editor = editorRef.current;
    if (!editor) return { start: value.length, end: value.length };

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return { start: value.length, end: value.length };

    const range = selection.getRangeAt(0);

    // Get start position
    const preStartRange = range.cloneRange();
    preStartRange.selectNodeContents(editor);
    preStartRange.setEnd(range.startContainer, range.startOffset);
    const start = preStartRange.toString().length;

    // Get end position
    const preEndRange = range.cloneRange();
    preEndRange.selectNodeContents(editor);
    preEndRange.setEnd(range.endContainer, range.endOffset);
    const end = preEndRange.toString().length;

    return { start, end };
  }, [value.length]);

  // Convenience: get just cursor position (start of selection)
  const getCursorPosition = useCallback((): number => {
    return getSelectionRange().start;
  }, [getSelectionRange]);

  // Set cursor position in the contenteditable
  // biome-ignore lint/correctness/useExhaustiveDependencies: editorRef is a stable ref
  const setCursorPosition = useCallback((position: number) => {
    const editor = editorRef.current;
    if (!editor) return;

    // Ensure editor is focused so the caret is visible
    if (document.activeElement !== editor) {
      editor.focus();
    }

    const selection = window.getSelection();
    if (!selection) return;

    // Walk through text nodes to find the right position
    let currentPos = 0;
    const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT, null);
    let node: Text | null = walker.nextNode() as Text | null;

    while (node) {
      const nodeLength = node.textContent?.length || 0;
      if (currentPos + nodeLength >= position) {
        const range = document.createRange();
        range.setStart(node, position - currentPos);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        return;
      }
      currentPos += nodeLength;
      node = walker.nextNode() as Text | null;
    }

    // If position is beyond text, put cursor at end
    const range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  }, []);

  // Remove injected provisional text spans from the editor
  // biome-ignore lint/correctness/useExhaustiveDependencies: editorRef is a stable ref
  const stripProvisionalSpans = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    for (const el of Array.from(editor.querySelectorAll('[data-provisional]'))) {
      el.remove();
    }
  }, []);

  // Imperatively update the cursor overlay position (no React state, no re-renders)
  // biome-ignore lint/correctness/useExhaustiveDependencies: refs are stable
  const syncCursorOverlay = useCallback(() => {
    const overlay = cursorOverlayRef.current;
    if (!overlay) return;

    const editor = editorRef.current;
    const container = containerRef.current;
    if (!editor || !container) {
      overlay.style.display = 'none';
      return;
    }

    // Only show when editor is focused
    if (document.activeElement !== editor && !editor.contains(document.activeElement)) {
      overlay.style.display = 'none';
      return;
    }

    // Slow blink when recording, normal blink when editing
    overlay.style.animation = isRecordingRef.current
      ? 'recording-cursor-blink 1.4s step-end infinite'
      : 'recording-cursor-blink 1s step-end infinite';

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) {
      // Still show cursor at editor start position as fallback
      const editorRect = editor.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      overlay.style.display = 'block';
      overlay.style.top = `${editorRect.top - containerRect.top}px`;
      overlay.style.left = `${editorRect.left - containerRect.left}px`;
      overlay.style.height = '24px';
      return;
    }

    const range = sel.getRangeAt(0);
    const caretRect = range.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    if (!caretRect.height) {
      // Zero-height rect (empty editor or collapsed range) — use editor position
      const editorRect = editor.getBoundingClientRect();
      overlay.style.display = 'block';
      overlay.style.top = `${editorRect.top - containerRect.top}px`;
      overlay.style.left = `${editorRect.left - containerRect.left}px`;
      overlay.style.height = '24px';
    } else {
      overlay.style.display = 'block';
      overlay.style.top = `${caretRect.top - containerRect.top}px`;
      overlay.style.left = `${caretRect.left - containerRect.left}px`;
      overlay.style.height = `${caretRect.height}px`;
    }
  }, []);

  // Handle input changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: editorRef is a stable ref
  const handleInput = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;

    // Strip any injected provisional text before reading
    stripProvisionalSpans();

    const newValue = editor.innerText || '';
    cursorPositionRef.current = getCursorPosition();
    lastValueRef.current = newValue;
    onChange(newValue);
    onCursorPositionChange?.(cursorPositionRef.current);
  }, [onChange, onCursorPositionChange, getCursorPosition]);

  // Track cursor position and selection on selection change
  // biome-ignore lint/correctness/useExhaustiveDependencies: editorRef is a stable ref
  const handleSelectionChange = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;

    // Only update if editor is focused
    if (document.activeElement === editor || editor.contains(document.activeElement)) {
      const range = getSelectionRange();
      cursorPositionRef.current = range.start;
      onCursorPositionChange?.(range.start);
      onSelectionChange?.(range);
      syncCursorOverlay();
    }
  }, [getSelectionRange, onCursorPositionChange, onSelectionChange, syncCursorOverlay]);

  // Convert text to HTML (newlines to <br> tags, pending chunks highlighted)
  const textToHtml = useCallback((text: string, pending: PendingRefinement[]): string => {
    // First escape HTML entities
    let html = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    // Wrap pending refinement chunks in styled spans (search from end to preserve positions)
    // Sort by position in text (later first) to avoid position shifting
    const pendingWithPositions = pending
      .map((p) => ({
        ...p,
        escapedText: p.text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'),
      }))
      .map((p) => ({
        ...p,
        position: html.lastIndexOf(p.escapedText),
      }))
      .filter((p) => p.position !== -1)
      .sort((a, b) => b.position - a.position); // Sort descending by position

    for (const p of pendingWithPositions) {
      const before = html.substring(0, p.position);
      const after = html.substring(p.position + p.escapedText.length);
      // Blue shimmer effect + small spinner
      html = `${before}<span class="pending-refinement" data-pending-id="${p.id}">${p.escapedText}<span class="pending-spinner"></span></span>${after}`;
    }

    // Convert newlines to <br>
    return html.replace(/\n/g, '<br>');
  }, []);

  // Update content when value or pending refinements change
  // biome-ignore lint/correctness/useExhaustiveDependencies: editorRef is a stable ref
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    // Strip provisional spans before reading to get clean text comparison
    stripProvisionalSpans();
    const currentText = editor.innerText || '';

    // Update if value differs OR pending refinements changed (to update highlighting)
    if (value !== currentText || pendingRefinements.length > 0 || editor.querySelector('.pending-refinement')) {
      // Save cursor position relative to end (for appending)
      const wasAtEnd = cursorPositionRef.current >= currentText.length;

      // Update content - use innerHTML to support <br> tags and pending highlights
      editor.innerHTML = textToHtml(value, pendingRefinements);
      lastValueRef.current = value;

      // Restore cursor position
      if (targetCursorPosition != null && targetCursorPosition <= value.length) {
        // Parent explicitly requested cursor position (e.g. after insert-at-cursor)
        setCursorPosition(targetCursorPosition);
        cursorPositionRef.current = targetCursorPosition;
      } else if (wasAtEnd) {
        // Keep cursor at end if it was at end
        setCursorPosition(value.length);
        cursorPositionRef.current = value.length;
      } else {
        // Try to maintain cursor position
        setCursorPosition(Math.min(cursorPositionRef.current, value.length));
      }
    } else {
      // Keep lastValueRef in sync even if content matches
      lastValueRef.current = value;
    }

    // Always sync cursor overlay after content/cursor updates
    requestAnimationFrame(syncCursorOverlay);
  }, [
    value,
    pendingRefinements,
    targetCursorPosition,
    setCursorPosition,
    textToHtml,
    stripProvisionalSpans,
    syncCursorOverlay,
  ]);

  // Inject provisional text at cursor position when in insert/replace mode
  // biome-ignore lint/correctness/useExhaustiveDependencies: editorRef is a stable ref, stripProvisionalSpans is stable
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    // Clean up previous provisional spans
    stripProvisionalSpans();

    // Only inject inline when we have a target cursor position (insert/replace mode)
    if (!provisionalText || targetCursorPosition == null) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);

    const provisionalSpan = document.createElement('span');
    provisionalSpan.setAttribute('data-provisional', 'true');
    provisionalSpan.setAttribute('contenteditable', 'false');
    provisionalSpan.className = 'pointer-events-none text-slate-400 italic';
    provisionalSpan.textContent = provisionalText;
    range.insertNode(provisionalSpan);

    // Move cursor after the provisional span so next insert goes to the right place
    range.setStartAfter(provisionalSpan);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  }, [provisionalText, targetCursorPosition, value, stripProvisionalSpans]);

  // Listen for selection changes
  useEffect(() => {
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, [handleSelectionChange]);

  // Focus editor when recording starts and sync cursor
  // biome-ignore lint/correctness/useExhaustiveDependencies: editorRef is a stable ref
  useEffect(() => {
    if (isRecording && editorRef.current) {
      if (document.activeElement !== editorRef.current) {
        editorRef.current.focus();
      }
      requestAnimationFrame(() => syncCursorOverlay());
    } else {
      // Sync overlay (will hide if not focused, show if focused)
      syncCursorOverlay();
    }
  }, [isRecording, syncCursorOverlay]);

  // Show/hide cursor overlay on focus/blur
  // biome-ignore lint/correctness/useExhaustiveDependencies: editorRef is a stable ref
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const handleFocus = () => requestAnimationFrame(() => syncCursorOverlay());
    const handleBlur = () => {
      const overlay = cursorOverlayRef.current;
      if (overlay) overlay.style.display = 'none';
    };

    editor.addEventListener('focus', handleFocus);
    editor.addEventListener('blur', handleBlur);
    return () => {
      editor.removeEventListener('focus', handleFocus);
      editor.removeEventListener('blur', handleBlur);
    };
  }, [syncCursorOverlay]);

  // Handle paste to strip formatting
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  }, []);

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      {/* Styles for pending refinement highlighting */}
      <style>{`
        .pending-refinement {
          position: relative;
          border-radius: 4px;
          padding: 2px 4px;
          margin: 0 -4px;
          background: rgba(59, 130, 246, 0.08);
        }
        .pending-refinement::before {
          content: 'Yfirfer talgreiningu...';
          position: absolute;
          top: -18px;
          left: 0;
          font-size: 11px;
          color: rgb(59, 130, 246);
          opacity: 0.8;
          white-space: nowrap;
          pointer-events: none;
        }
        .pending-spinner {
          display: inline-block;
          width: 10px;
          height: 10px;
          margin-left: 6px;
          vertical-align: middle;
          border: 1.5px solid rgba(59, 130, 246, 0.2);
          border-top-color: rgb(59, 130, 246);
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes recording-cursor-blink {
          0%, 70% { opacity: 1; }
          71%, 100% { opacity: 0; }
        }
      `}</style>
      {/* Main editable area with inline provisional text */}
      <div className="min-h-[300px] w-full whitespace-pre-wrap text-lg leading-relaxed">
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onPaste={handlePaste}
          className="inline text-slate-900 outline-none"
          style={{ caretColor: 'transparent' }}
        />
        {/* Provisional text shown inline after editor (append mode only - insert mode injects via DOM) */}
        {provisionalText && targetCursorPosition == null && (
          <span className="pointer-events-none text-slate-400 italic">{provisionalText}</span>
        )}
      </div>

      {/* Custom recording cursor overlay — positioned imperatively via ref */}
      <div
        ref={cursorOverlayRef}
        className="pointer-events-none absolute z-10"
        style={{
          display: 'none',
          width: 3,
          backgroundColor: '#2563eb',
          borderRadius: 1,
          animation: 'recording-cursor-blink 1.4s step-end infinite',
        }}
      />

      {/* Placeholder */}
      {!value && !provisionalText && !isRecording && (
        <div className="pointer-events-none absolute inset-0 text-slate-400">
          Ýttu á hljóðnemann til að byrja að tala...
        </div>
      )}
    </div>
  );
}

/**
 * Hook to manage transcription with cursor-aware insertion
 */
export function useTranscriptionEditor(initialValue = '') {
  const [text, setText] = React.useState(initialValue);
  const cursorPositionRef = useRef<number>(0);

  const insertAtCursor = useCallback((newText: string) => {
    setText((prev) => {
      const pos = cursorPositionRef.current;
      const before = prev.slice(0, pos);
      const after = prev.slice(pos);
      const result = before + newText + after;
      cursorPositionRef.current = pos + newText.length;
      return result;
    });
  }, []);

  const replaceAll = useCallback((newText: string) => {
    setText(newText);
    cursorPositionRef.current = newText.length;
  }, []);

  const updateCursorPosition = useCallback((position: number) => {
    cursorPositionRef.current = position;
  }, []);

  return {
    text,
    setText,
    insertAtCursor,
    replaceAll,
    cursorPosition: cursorPositionRef.current,
    updateCursorPosition,
  };
}
